import React, { useState, useEffect } from 'react';
import "../../css/Teachers.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaSearch, FaChalkboardTeacher, FaEdit, FaTrash, FaFilter, FaTimes } from 'react-icons/fa';

function Teachers() {
    const [teachers, setTeachers] = useState([]);
    const [filteredTeachers, setFilteredTeachers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOption, setFilterOption] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentTeacher, setCurrentTeacher] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teachers", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch teachers");
            }

            const data = await response.json();
            setTeachers(data);
            setFilteredTeachers(data);
        } catch (error) {
            console.error("Error fetching teachers:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle opening the edit modal
    const handleEditClick = (teacher) => {
        setCurrentTeacher(teacher);
        setEditFormData({ ...teacher });
        setShowEditModal(true);
    };

    // Handle opening the delete modal
    const handleDeleteClick = (teacher) => {
        setCurrentTeacher(teacher);
        setShowDeleteModal(true);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: value
        });
    };

    // Handle form submission for update
    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/teachers/${currentTeacher._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update teacher");
            }

            const data = await response.json();

            // Update the teachers array with updated data
            const updatedTeachers = teachers.map(teacher =>
                teacher._id === currentTeacher._id ? data.teacher : teacher
            );

            setTeachers(updatedTeachers);
            setFilteredTeachers(updatedTeachers);

            toast.success("Teacher updated successfully!");
            setShowEditModal(false);
        } catch (error) {
            console.error("Error updating teacher:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle teacher deletion
    const handleDeleteConfirm = async () => {
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/teachers/${currentTeacher._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete teacher");
            }

            // Remove the deleted teacher from state
            const updatedTeachers = teachers.filter(teacher => teacher._id !== currentTeacher._id);
            setTeachers(updatedTeachers);
            setFilteredTeachers(updatedTeachers);

            toast.success("Teacher deleted successfully!");
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting teacher:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search and filtering
    useEffect(() => {
        let results = teachers;

        // Apply search
        if (searchTerm) {
            results = results.filter(teacher =>
                (teacher.firstName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (teacher.lastName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (teacher.employeeId?.toString() || "").includes(searchTerm) ||
                (teacher.department?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (teacher.position?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (teacher.email?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase())
            );
        }

        // Apply filters
        if (filterOption !== 'all') {
            if (filterOption === 'male' || filterOption === 'female' || filterOption === 'other') {
                results = results.filter(teacher =>
                    teacher.gender?.toString().toLowerCase() === filterOption
                );
            } else {
                // Filter by department
                results = results.filter(teacher =>
                    teacher.department?.toString().toLowerCase() === filterOption.toLowerCase()
                );
            }
        }

        setFilteredTeachers(results);
    }, [searchTerm, filterOption, teachers]);

    // Get unique departments for filter dropdown
    const departments = [...new Set(teachers.map(teacher => teacher.department?.toString()))]
        .filter(Boolean)
        .sort();

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (e) => {
        setFilterOption(e.target.value);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Format date safely
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    };

    // Format date for input field
    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch (e) {
            return "";
        }
    };

    // Format salary with currency symbol
    const formatSalary = (salary) => {
        if (!salary && salary !== 0) return "N/A";
        return `₹${Number(salary).toLocaleString()}`;
    };

    return (
        <div className="teachers-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="teachers-header">
                <div className="teachers-title-container">
                    <FaChalkboardTeacher className="teachers-icon" />
                    <h1 className="teachers-title">Faculty Directory</h1>
                </div>

                <div className="teachers-controls">
                    <div className="teachers-search-container">
                        <FaSearch className="teachers-search-icon" />
                        <input
                            type="text"
                            className="teachers-search-input"
                            placeholder="Search by name, ID, department, position..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>

                    <button className="teachers-filter-toggle-btn" onClick={toggleFilters}>
                        <FaFilter /> Filters
                    </button>
                </div>

                {showFilters && (
                    <div className="teachers-filter-options">
                        <select
                            value={filterOption}
                            onChange={handleFilterChange}
                            className="teachers-filter-select"
                        >
                            <option value="all">All Faculty</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                            {departments.length > 0 && (
                                <optgroup label="Departments">
                                    {departments.map(department => (
                                        <option key={department} value={department}>{department}</option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    </div>
                )}

                <div className="teachers-stats">
                    <div className="teachers-stat-box">
                        <span className="teachers-stat-number">{filteredTeachers.length}</span>
                        <span className="teachers-stat-label">Faculty Members</span>
                    </div>
                    {filterOption !== 'all' && (
                        <div className="teachers-stat-box highlight">
                            <span className="teachers-stat-number">{filterOption}</span>
                            <span className="teachers-stat-label">
                                {filterOption === 'male' || filterOption === 'female' || filterOption === 'other'
                                    ? 'Gender'
                                    : 'Department'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {filteredTeachers.length === 0 && !isLoading ? (
                <div className="teachers-empty">
                    <div className="teachers-empty-icon">
                        <FaSearch size={48} color="#a0aec0" />
                    </div>
                    <p className="teachers-empty-text">No faculty members found matching your criteria.</p>
                    {searchTerm && (
                        <button className="teachers-reset-search" onClick={() => setSearchTerm('')}>
                            Clear Search
                        </button>
                    )}
                </div>
            ) : (
                <div className="teachers-grid">
                    {filteredTeachers.map((teacher, index) => {
                        // Get first character of names safely
                        const firstInitial = teacher.firstName ? teacher.firstName.charAt(0) : '';
                        const lastInitial = teacher.lastName ? teacher.lastName.charAt(0) : '';
                        const gender = teacher.gender?.toString().toLowerCase() || 'other';

                        return (
                            <div key={index} className="teachers-card">
                                <div className={`teachers-card-header ${gender === 'female' ? 'female' : 'male'}`}>
                                    <div className="teachers-avatar">
                                        {firstInitial}{lastInitial}
                                    </div>
                                    <div className="teachers-header-info">
                                        <h2 className="teachers-name">
                                            {teacher.firstName || ''} {teacher.lastName || ''}
                                        </h2>
                                        <span className="teachers-position">
                                            {teacher.position || 'Faculty'}
                                        </span>
                                    </div>
                                </div>

                                <div className="teachers-card-body">
                                    <div className="teachers-quick-info">
                                        <span className="teachers-department-badge">{teacher.department || 'Department'}</span>
                                        <span className="teachers-id-badge">ID: {teacher.employeeId || 'N/A'}</span>
                                    </div>

                                    <div className="teachers-info-section">
                                        <h3 className="teachers-section-title">Professional Info</h3>
                                        <div className="teachers-info-group">
                                            <span className="teachers-info-label">Qualification:</span>
                                            <span className="teachers-info-value">{teacher.qualification || 'N/A'}</span>
                                        </div>
                                        <div className="teachers-info-group">
                                            <span className="teachers-info-label">Experience:</span>
                                            <span className="teachers-info-value">{teacher.experience || 'N/A'}</span>
                                        </div>
                                        <div className="teachers-info-group">
                                            <span className="teachers-info-label">Specialization:</span>
                                            <span className="teachers-info-value">{teacher.specialization || 'N/A'}</span>
                                        </div>
                                        <div className="teachers-info-group">
                                            <span className="teachers-info-label">Salary:</span>
                                            <span className="teachers-info-value teachers-salary">{formatSalary(teacher.salary)}</span>
                                        </div>
                                    </div>

                                    <div className="teachers-info-section">
                                        <h3 className="teachers-section-title">Contact Info</h3>
                                        <div className="teachers-info-group">
                                            <span className="teachers-info-label">Email:</span>
                                            <span className="teachers-info-value">{teacher.email || 'N/A'}</span>
                                        </div>
                                        <div className="teachers-info-group">
                                            <span className="teachers-info-label">Phone:</span>
                                            <span className="teachers-info-value">{teacher.phone || 'N/A'}</span>
                                        </div>
                                        <div className="teachers-info-group">
                                            <span className="teachers-info-label">DOB:</span>
                                            <span className="teachers-info-value">{formatDate(teacher.dob)}</span>
                                        </div>
                                        {teacher.bloodGroup && (
                                            <div className="teachers-info-group">
                                                <span className="teachers-info-label">Blood Group:</span>
                                                <span className="teachers-info-value teachers-blood-group">{teacher.bloodGroup}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="teachers-info-section">
                                        <h3 className="teachers-section-title">Address</h3>
                                        <div className="teachers-info-group teachers-address">
                                            <span className="teachers-info-value">{teacher.address || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="teachers-info-group teachers-joining-date">
                                        <span className="teachers-info-label">Joined:</span>
                                        <span className="teachers-info-value">{formatDate(teacher.joiningDate)}</span>
                                    </div>
                                </div>

                                <div className="teachers-card-footer">
                                    <button className="teachers-action-btn teachers-edit-btn" onClick={() => handleEditClick(teacher)}>
                                        <FaEdit /> Edit
                                    </button>
                                    <button className="teachers-action-btn teachers-delete-btn" onClick={() => handleDeleteClick(teacher)}>
                                        <FaTrash /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <div className="teachers-modal-overlay">
                    <div className="teachers-modal-container">
                        <div className="teachers-modal-header">
                            <h2>Edit Faculty Member</h2>
                            <button className="teachers-close-btn" onClick={() => setShowEditModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="teachers-modal-body">
                            <form onSubmit={handleUpdateSubmit}>
                                <div className="teachers-form-row">
                                    <div className="teachers-form-group half">
                                        <label>First Name:</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={editFormData.firstName || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="teachers-form-group half">
                                        <label>Last Name:</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={editFormData.lastName || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="teachers-form-row">
                                    <div className="teachers-form-group half">
                                        <label>Employee ID:</label>
                                        <input
                                            type="text"
                                            name="employeeId"
                                            value={editFormData.employeeId || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="teachers-form-group half">
                                        <label>Gender:</label>
                                        <select
                                            name="gender"
                                            value={editFormData.gender || ''}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="teachers-form-row">
                                    <div className="teachers-form-group half">
                                        <label>Department:</label>
                                        <input
                                            type="text"
                                            name="department"
                                            value={editFormData.department || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="teachers-form-group half">
                                        <label>Position:</label>
                                        <input
                                            type="text"
                                            name="position"
                                            value={editFormData.position || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="teachers-form-row">
                                    <div className="teachers-form-group half">
                                        <label>Email:</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={editFormData.email || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="teachers-form-group half">
                                        <label>Phone:</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            value={editFormData.phone || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="teachers-form-row">
                                    <div className="teachers-form-group half">
                                        <label>Date of Birth:</label>
                                        <input
                                            type="date"
                                            name="dob"
                                            value={formatDateForInput(editFormData.dob)}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="teachers-form-group half">
                                        <label>Joining Date:</label>
                                        <input
                                            type="date"
                                            name="joiningDate"
                                            value={formatDateForInput(editFormData.joiningDate)}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="teachers-form-row">
                                    <div className="teachers-form-group half">
                                        <label>Qualification:</label>
                                        <input
                                            type="text"
                                            name="qualification"
                                            value={editFormData.qualification || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="teachers-form-group half">
                                        <label>Experience:</label>
                                        <input
                                            type="text"
                                            name="experience"
                                            value={editFormData.experience || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="teachers-form-row">
                                    <div className="teachers-form-group half">
                                        <label>Specialization:</label>
                                        <input
                                            type="text"
                                            name="specialization"
                                            value={editFormData.specialization || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="teachers-form-group half">
                                        <label>Salary:</label>
                                        <input
                                            type="number"
                                            name="salary"
                                            value={editFormData.salary || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="teachers-form-group">
                                    <label>Blood Group:</label>
                                    <select
                                        name="bloodGroup"
                                        value={editFormData.bloodGroup || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Blood Group</option>
                                        <option value="A+">A+</option>
                                        <option value="A-">A-</option>
                                        <option value="B+">B+</option>
                                        <option value="B-">B-</option>
                                        <option value="AB+">AB+</option>
                                        <option value="AB-">AB-</option>
                                        <option value="O+">O+</option>
                                        <option value="O-">O-</option>
                                    </select>
                                </div>

                                <div className="teachers-form-group">
                                    <label>Address:</label>
                                    <textarea
                                        name="address"
                                        value={editFormData.address || ''}
                                        onChange={handleInputChange}
                                        required
                                    ></textarea>
                                </div>

                                <div className="teachers-modal-footer">
                                    <button type="button" className="teachers-cancel-btn" onClick={() => setShowEditModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="teachers-save-btn">
                                        Update Faculty
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="teachers-modal-overlay">
                    <div className="teachers-modal-container teachers-delete-modal">
                        <div className="teachers-modal-header">
                            <h2>Delete Faculty Member</h2>
                            <button className="teachers-close-btn" onClick={() => setShowDeleteModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="teachers-modal-body">
                            <div className="teachers-delete-warning">
                                <FaTrash className="teachers-delete-icon" />
                                <p>Are you sure you want to delete {currentTeacher?.firstName} {currentTeacher?.lastName}?</p>
                                <p className="teachers-warning-text">This action cannot be undone.</p>
                            </div>
                            <div className="teachers-modal-footer">
                                <button className="teachers-cancel-btn" onClick={() => setShowDeleteModal(false)}>
                                    Cancel
                                </button>
                                <button className="teachers-delete-confirm-btn" onClick={handleDeleteConfirm}>
                                    Delete Faculty
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Teachers;